<script setup lang="ts">
const { toggleModal } = useLayoutStore();

const { walletIsLoading, walletBonusFormatted, showMoneyFormat } = useWallet();

const { getRolloverData } = useWalletStore();

const { bonusWallet, rollover, rolloverAccomplished } =
  storeToRefs(useWalletStore());

const amountBet = computed(() => {
  return `${showMoneyFormat({ value: (rollover.value?.amount || 0) / 100 })} /
  ${showMoneyFormat({
    value:
      (bonusWallet.value.credit_hold || 12) * (rollover.value.multiplier || 0),
  })}`;
});

const totalTime = computed(() => {
  const now = new Date();
  const expireDatetime = new Date(bonusWallet.value.expiry_datetime);

  return expireDatetime.getTime() > now.getTime()
    ? (expireDatetime.getTime() - now.getTime()) / 1000
    : 0;
});

const necessaryRollover = computed(() => {
  return rollover.value.rollover_count || 0;
});

const currentRolloverCount = computed(() => {
  return rollover.value.count || 0;
});

const rolloverRoundSuccess = computed(() => {
  if (!currentRolloverCount.value) return false;

  return currentRolloverCount.value >= necessaryRollover.value;
});

const rolloverAmoumtMaxValue = computed(() => {
  return (
    (bonusWallet.value.credit_hold || 12) * (rollover.value.multiplier || 0)
  );
});

const walletHasBonus = computed<boolean>(() => {
  return !!bonusWallet.value.credit && totalTime.value > 0;
});

onMounted(() => {
  getRolloverData();
});
</script>

<template>
  <UserBox v-if="walletHasBonus">
    <div class="flex justify-start items-stretch">
      <div :class="$style.bonusInfoxBox">
        <div :class="$style.topContent">
          <UserWalletBonusRulesButton
            class="absolute top-0 right-0"
            :is-rounded="true"
            :show-icon="true"
            :show-text="false"
            @toggle-withdraw-rules="toggleModal('bonus-rules')"
          />

          <span :class="$style.currencyBonus">
            <DepositCurrencyButton
              :is-bonus-icon="true"
              :user-currency="$t('account.wallet.bonus')"
            />
          </span>

          <strong
            :class="[$style.walletAmount, { 'opacity-30': walletIsLoading }]"
          >
            {{ walletBonusFormatted }}
          </strong>

          <div
            v-if="totalTime"
            :class="$style.bonusCountdown"
          >
            <WidgetsCountdown :total-time="totalTime">
              <template #process="{ time }">
                <span :class="$style.countdownTitle">
                  {{ $t('account.wallet.bonus_expire_time') }}
                </span>
                <span :class="$style.countdownTime">
                  {{ `${time.hours}h : ${time.minutes}m : ${time.seconds}s` }}
                </span>
              </template>
              <template #finish>
                <span :class="$style.countdownTitle">
                  {{ $t('account.wallet.bonus_expired') }}
                </span>
              </template>
            </WidgetsCountdown>
          </div>

          <div :class="$style.bonusRolloverType">
            <p v-if="rollover.first_transaction === 'casino'">
              <NuxtIcon name="info" />
              <span>{{ $t('account.wallet.withdraw_bonus_available') }}</span>
            </p>
            <p v-else-if="rollover.first_transaction === 'sports'">
              <NuxtIcon name="info" />
              <span>
                {{ $t('account.wallet.withdraw_bonus_available_sports') }}
              </span>
            </p>
            <p v-else>
              <NuxtIcon name="info" />
              <span>
                {{ $t('account.wallet.withdraw_rollover_none') }}
              </span>
            </p>
          </div>
        </div>

        <div :class="$style.walletBoxBtns">
          <NuxtLink
            v-if="rollover.first_transaction === 'sports'"
            :class="[$style.walletBtn, $style.accessBtn]"
            :to="localeLink('/sports')"
          >
            {{ $t('account.wallet.access_sports') }}
          </NuxtLink>
          <NuxtLink
            v-else
            :class="[$style.walletBtn, $style.accessBtn]"
            :to="localeLink('/games/category/jogar-com-bonus')"
          >
            {{ $t('account.wallet.access_games') }}
          </NuxtLink>

          <button
            v-if="rolloverAccomplished"
            :class="[$style.walletBtn, $style.withdrawBtn]"
            @click="toggleModal('bonus-withdraw')"
          >
            <span
              :class="$style.text"
              v-html="$t('account.wallet.withdraw_bonuses')"
            />
          </button>
          <button
            v-else
            :class="[$style.walletBtn, $style.withdrawBtn]"
            disabled
          >
            <span
              :class="$style.text"
              v-html="$t('account.wallet.withdraw_bonuses')"
            />
          </button>
        </div>
      </div>

      <div :class="$style.rolloverProgressBox">
        <span
          :class="[
            $style.rolloverStatus,
            !rollover.success ? 'opacity-40' : 'opacity-100',
          ]"
        >
          {{ $t('account.wallet.rollover') }}
          <span v-if="rollover.first_transaction === 'casino'">
            {{ $t('account.wallet.casino').toLocaleLowerCase() }}
          </span>
          <span v-if="rollover.first_transaction === 'sports'">
            {{ $t('account.wallet.sports').toLocaleLowerCase() }}
          </span>
        </span>

        <div
          :class="[
            $style.bonusRolloverContainer,
            { [$style.isInactive]: !rollover.success },
          ]"
        >
          <div :class="$style.progressItem">
            <div :class="$style.rollLabel">
              <span :class="$style.rollValue">
                {{ $t('account.wallet.amount_bet') }}
              </span>
              <span
                :class="[
                  $style.rollValue,
                  $style.isValue,
                  rolloverRoundSuccess ? 'text-primary' : 'text-texts',
                ]"
              >
                {{ amountBet }}
              </span>
              <span :class="$style.rollValue">
                {{ $t('account.wallet.rollover_target') }}
              </span>
            </div>
            <UserWalletProgressBar
              :class="$style.rollProgress"
              :height="10"
              :max="rolloverAmoumtMaxValue"
              :progress="rollover.amount! / 100"
            />
          </div>

          <div :class="$style.progressItem">
            <div :class="$style.rollLabel">
              <span :class="$style.rollValue">
                {{ $t('account.wallet.rounds_played') }}
              </span>
              <span
                :class="[
                  $style.rollValue,
                  $style.isValue,
                  rolloverRoundSuccess ? 'text-primary' : 'text-texts',
                ]"
              >
                {{ currentRolloverCount }} / {{ necessaryRollover }}
              </span>
              <span :class="$style.rollValue">
                {{ $t('account.wallet.play_goal') }}
              </span>
            </div>
            <UserWalletProgressBar
              :class="$style.rollProgress"
              :height="10"
              :max="necessaryRollover"
              :progress="currentRolloverCount || 0"
            />
          </div>
        </div>
      </div>
    </div>
  </UserBox>
</template>

<style module lang="postcss">
.bonusInfoxBox {
  @apply min-w-[280px] md:min-w-[280px] 2xl:min-w-[360px] relative min-h-full flex flex-col;

  .topContent {
    @apply pb-2 relative flex flex-col items-start justify-start;

    .currencyBonus {
      @apply text-texts/70 inline-flex pb-1;
    }

    .walletAmount {
      @apply inline-block text-xl text-texts leading-none font-medium lg:text-3xl pr-16 md:pr-24 pb-1;
    }

    .bonusCountdown {
      @apply text-2xs mt-1 mb-4;
      .countdownTitle {
        @apply bg-error rounded-l py-1 px-1.5 font-semibold;
      }
      .countdownTime {
        @apply bg-white text-black rounded-r py-1 px-2 font-bold;
      }
    }

    .bonusRolloverType {
      @apply flex flex-col gap-3;
      p {
        @apply flex gap-2 items-center text-texts/70 text-2xs;
      }
    }
  }

  .walletBoxBtns {
    @apply flex items-center justify-between gap-2 w-full mt-auto;

    .walletBtn {
      @apply py-2 px-4 rounded-md flex-1 min-w-28 text-xs md:text-sm flex items-center justify-center gap-2 whitespace-nowrap;

      &.accessBtn {
        @apply bg-primary/15 text-primary/70 font-semibold;
      }

      &.withdrawBtn {
        @apply bg-white text-bg-primary font-semibold;

        &[disabled]:not([disabled='false']) {
          @apply opacity-40 cursor-not-allowed;
        }

        &.isCountdown {
          @apply flex flex-col items-center gap-1 px-3 py-0 cursor-not-allowed flex-1 min-h-full;
        }

        .icon {
          @apply text-sm;
        }
      }
    }
  }
}

.rolloverProgressBox {
  @apply border-solid border-l border-texts/10 min-w-[320px] md:min-w-[250px] 2xl:min-w-[360px] pl-5 ml-5 flex flex-col items-start justify-center flex-1;

  .rolloverStatus {
    @apply py-[3px] px-1.5 leading-3 inline-block rounded text-2xs font-semibold bg-warning text-bg-primary;
  }

  .bonusRolloverContainer {
    @apply mt-2 w-full;

    &.isInactive {
      @apply opacity-50;
    }

    .progressItem {
      @apply flex flex-col py-1.5 whitespace-nowrap;

      .rollLabel {
        @apply flex items-center justify-between gap-2 w-full;

        .rollValue {
          @apply text-2xs text-center;

          &.isValue {
            @apply font-semibold;
          }
        }
      }

      .rollProgress {
        @apply col-span-3;
      }
    }
  }
}
</style>
